import React from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'
import {graphql, StaticQuery} from 'gatsby'
import {imageUrlFor} from "../lib/image-url"
import {buildImageObj} from "../lib/helpers"

import {getJsonLD} from "./JsonLD"

const detailsQuery = graphql`
  query SEOQuery {
    site: sanitySiteSettings(_id: { regex: "/(drafts.|)siteSettings/" }) {
      title
      description
      siteUrl
      seoTitleSuffix
      ogImage {
        asset {
          _id
        }
      }
      twitterImage {
        asset {
          _id
        }
      }
      logoImage {
        asset {
          _id
        }
      }
    }
    company: sanityCompanyInfo {
      email
      facebook
      instagram
      pinterest
      twitter
      name
    }
  }
`

function Seo(
  {
    description,
    lang,
    meta,
    title,
    type,
    pathname,
    image,
    category,
    page,
    post,
    tag,
  },
) {

  return (
    <StaticQuery
      query={detailsQuery}
      render={data => {

        if (!data.site || !data.company) {
          return
        }

        const customTitle = title || data.site.title
        const metaDescription = description || data.site.description
        const pageType = type || 'website'
        const url = data.site.siteUrl + pathname
        const noIndex = pageType !== 'article' && pathname !== undefined && pathname !== '/ressources/'

        const defaultTwitterImage = data.site.twitterImage && imageUrlFor(buildImageObj(data.site.twitterImage))
          .width(600)
          .height(300)
          .auto('format')
          .quality(75)
          .fit('clip')
          .url()

        const defaultOgImage = data.site.ogImage && imageUrlFor(buildImageObj(data.site.ogImage))
          .width(1200)
          .height(630)
          .auto('format')
          .quality(75)
          .fit('clip')
          .url()

        const twitterImage = image && imageUrlFor(buildImageObj(image))
          .width(600)
          .height(300)
          .auto('format')
          .quality(75)
          .fit('clip')
          .url()

        const ogImage = image && imageUrlFor(buildImageObj(image))
          .width(1200)
          .height(630)
          .auto('format')
          .quality(75)
          .fit('clip')
          .url()

        return (
          <Helmet
            htmlAttributes={{
              lang,
            }}
            title={customTitle}
            titleTemplate={customTitle === data.site.title ? '%s' : `%s ${data.site.seoTitleSuffix}`}
            meta={[
              {
                name: 'description',
                content: metaDescription,
              },
              {
                property: 'og:site_name',
                content: 'Moneylo',
              },
              {
                property: 'og:locale',
                content: 'fr_FR',
              },
              {
                property: 'og:title',
                content: customTitle,
              },
              {
                property: 'og:description',
                content: metaDescription,
              },
              {
                property: 'og:type',
                content: pageType,
              },
              {
                property: 'og:image',
                content: ogImage || defaultOgImage,
              },
              {
                property: 'og:image:width',
                content: "1200",
              },
              {
                property: 'og:image:height',
                content: "630",
              },
              {
                property: 'og:url',
                content: url,
              },
              {
                name: 'twitter:site',
                content: '@MoneyloBlog',
              },
              {
                name: 'twitter:card',
                content: 'summary_large_image',
              },
              {
                name: 'twitter:creator',
                content: '@MoneyloBlog',
              },
              {
                name: 'twitter:title',
                content: customTitle,
              },
              {
                name: 'twitter:description',
                content: metaDescription,
              },
              {
                name: 'twitter:image',
                content: twitterImage || defaultTwitterImage,
              },
            ]
              .concat(
                pageType === 'article'
                  ? [
                    {
                      name: 'article:section',
                      content: category.title,
                    }, {
                      name: 'article:published_time',
                      content: post.publishedAt,
                    }, {
                      name: 'article:author',
                      content: post.author,
                    }, {
                      name: 'article:tag',
                      content: post.tags,
                    },
                  ]
                  : [],
              )
              .concat(
                noIndex ? [{
                  name: 'Algolia crawler',
                  content: 'noindex',
                }] : [],
              )
              .concat(
                post && post._updatedAt ?
                  [{
                    name: 'article:modified_time',
                    content: post._updatedAt,
                  }, {
                    name: 'og:updated_time',
                    content: post._updatedAt,
                  },
                  ] : [],
              )
              .concat(meta)}>
            <script type="application/ld+json">
              {pathname && getJsonLD({
                site: data.site,
                company: data.company,
                description,
                title: customTitle,
                url,
                imageUrl: ogImage || defaultOgImage,
                category,
                post,
                tag,
                page,
                pathname,
              })}</script>
            <link rel="canonical"
                  href={post?.canonical || `https://moneylo.fr${pathname === '/' ? '' : pathname}`}/>
          </Helmet>
        )
      }}
    />
  )
}

Seo.defaultProps = {
  lang: 'fr',
  meta: [],
  keywords: [],
}

Seo.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string,
}

export default Seo
